import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { isVendedor, getUserContext } from "../../auth";
import { apiFetch } from "../../api";

function renderValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
    return String(v);
  if (Array.isArray(v)) return v.map((x) => renderValue(x)).join(", ");
  try {
    return JSON.stringify(v, null, 2);
  } catch (e) {
    return String(v);
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return String(dateStr);
}

function renderClientCard(obj) {
  if (!obj) return <Typography variant="body2"></Typography>;

  const labels = {
    telefono: "Teléfono",
    email: "E-mail",
    consulta: "Consulta 1",
    consulta2: "Consulta 2",
    entrega: "Entrega",
    precio_de_toma: "Precio toma",
    obtenido_de: "Obtenido de",
    fecha_carga: "Fecha de carga",
  };

  const rows = [
    ["telefono", "email"],
    ["consulta", "consulta2"],
    ["entrega", "precio_de_toma"],
    ["obtenido_de", "fecha_carga"],
  ];

  const getValue = (key) => {
    const v = obj[key];
    if (key === "fecha_carga") return formatDate(v);
    return renderValue(v);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: "flex",
            gap: 2,
            mb: 0.5,
          }}
        >
          {row.map((key) => (
            <Box key={key} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.75rem" }}
              >
                {labels[key] ?? key}:
              </Typography>{" "}
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}
              >
                {getValue(key)}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function EditConflictTransferDialog({
  open,
  onClose,
  existing,
  incoming,
  target,
  resolution = null,
  onResolved,
}) {
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState("apply_changes");
  
  // For transfer mode: first selected client is transfer target (green), rest are to delete (red)
  const [transferTargetId, setTransferTargetId] = React.useState(null);
  const [deleteIds, setDeleteIds] = React.useState(new Set());
  
  // For non-transfer mode (admin only): selected clients will be deleted
  const [selectedForDelete, setSelectedForDelete] = React.useState(new Set());

  const isSeller = isVendedor();
  const currentUser = getUserContext();
  const currentVendedorId =
    currentUser?.vendedor_id ?? currentUser?.id_vendedor ?? null;

  const existingList = React.useMemo(() => {
    if (!existing) return [];
    return Array.isArray(existing) ? existing : [existing];
  }, [existing]);

  // Check if a client belongs to the current seller
  const clientBelongsToSeller = (clientId) => {
    if (!isSeller) return true; // Admin can select any client
    const client = existingList.find((c) => c.id === clientId);
    return client?.vendedor?.id === currentVendedorId;
  };

  React.useEffect(() => {
    if (!open) return;
    setSelected("apply_changes");
    setLoading(false);
    setTransferTargetId(null);
    setDeleteIds(new Set());
    setSelectedForDelete(new Set());
  }, [open, existing]);

  // Handle client card click based on current mode
  const handleClientClick = (clientId) => {
    // If seller and client doesn't belong to them, do nothing
    if (isSeller && !clientBelongsToSeller(clientId)) {
      return;
    }

    if (selected === "transfer") {
      // Transfer mode: first click sets transfer target, subsequent clicks toggle delete
      // Only admin can use transfer
      if (isSeller) return;
      
      if (transferTargetId === null) {
        setTransferTargetId(clientId);
      } else if (transferTargetId === clientId) {
        setTransferTargetId(null);
        setDeleteIds(new Set());
      } else {
        const copy = new Set(deleteIds);
        if (copy.has(clientId)) {
          copy.delete(clientId);
        } else {
          copy.add(clientId);
        }
        setDeleteIds(copy);
      }
    } else if (selected === "apply_changes") {
      // Apply changes mode: toggle client for deletion
      const copy = new Set(selectedForDelete);
      if (copy.has(clientId)) {
        copy.delete(clientId);
      } else {
        copy.add(clientId);
      }
      setSelectedForDelete(copy);
    }
  };

  // Get client card style based on selection state
  const getClientCardStyle = (clientId) => {
    const canSelect = clientBelongsToSeller(clientId);
    
    const baseStyle = {
      mb: 1.5,
      p: 1.5,
      borderRadius: 2,
      cursor: canSelect ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
      opacity: canSelect ? 1 : 0.6,
      '&:hover': canSelect ? {
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      } : {},
    };

    if (selected === "transfer" && !isSeller) {
      if (transferTargetId === clientId) {
        return {
          ...baseStyle,
          background: 'rgba(76, 175, 80, 0.15)',
          border: '2px solid #4caf50',
        };
      } else if (deleteIds.has(clientId)) {
        return {
          ...baseStyle,
          background: 'rgba(244, 67, 54, 0.15)',
          border: '2px solid #f44336',
        };
      }
    } else if (selected === "apply_changes") {
      if (selectedForDelete.has(clientId)) {
        return {
          ...baseStyle,
          background: 'rgba(244, 67, 54, 0.15)',
          border: '2px solid #f44336',
        };
      }
    }

    return {
      ...baseStyle,
      background: 'rgba(0,0,0,0.03)',
      border: '1px solid rgba(0,0,0,0.08)',
    };
  };

  const actionCardStyle = (action) => ({
    borderRadius: 2,
    p: 1.5,
    cursor: "pointer",
    background:
      selected === action ? "rgba(25,118,210,0.16)" : "rgba(25,118,210,0.06)",
    border: selected === action ? "2px solid #1976d2" : "2px solid transparent",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(25,118,210,0.12)",
    },
  });

  const handleModeChange = (newMode) => {
    setSelected(newMode);
    setTransferTargetId(null);
    setDeleteIds(new Set());
    setSelectedForDelete(new Set());
  };

  const handleContinue = async () => {
    if (!target || !target.id) return;

    if (selected === "apply_changes") {
      // Apply changes to the edited client
      let delete_ids = isSeller ? [] : Array.from(selectedForDelete);

      let confirmMsg = "";
      if (delete_ids.length === 0) {
        confirmMsg = `Se aplicarán los cambios al cliente editado (se creará un duplicado).\n¿Continuar?`;
      } else {
        confirmMsg = `Se aplicarán los cambios al cliente editado y se eliminarán ${delete_ids.length} cliente(s) seleccionado(s).\n¿Continuar?`;
      }

      if (!window.confirm(confirmMsg)) return;

      setLoading(true);
      try {
        // First apply the changes
        const payload = incoming || {};
        const res = await apiFetch(`/api/clientes/${target.id}?force=keep_incoming`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Error aplicando cambios");
        }
        const data = await res.json();

        // Then delete selected clients if any (admin only)
        for (const id of delete_ids) {
          await apiFetch(`/api/clientes/${id}`, {
            method: "DELETE",
            credentials: "include",
          });
        }

        onResolved && onResolved({ kept: "incoming", created: data });
        onClose();
      } catch (err) {
        console.error("apply changes error", err);
        alert(err.message || "Error al aplicar cambios");
      } finally {
        setLoading(false);
      }
    } else if (selected === "transfer") {
      // Transfer mode (admin only)
      if (isSeller) {
        alert("Los vendedores no pueden realizar transferencias.");
        return;
      }

      if (!transferTargetId) {
        alert("Debes seleccionar el cliente desde el cual transferir (se marcará en verde)");
        return;
      }

      const additionalDeletes = deleteIds.size;
      let confirmMsg = "";
      if (additionalDeletes === 0) {
        confirmMsg = `Se transferirán los datos del cliente seleccionado al cliente editado.\n¿Continuar?`;
      } else {
        confirmMsg = `Se transferirán los datos y se eliminarán ${additionalDeletes} cliente(s) adicional(es).\n¿Continuar?`;
      }

      if (!window.confirm(confirmMsg)) return;

      setLoading(true);
      try {
        const payload = {
          source_id: transferTargetId,
          target_id: target.id,
          transfer_assignments: true,
        };
        const res = await apiFetch("/api/clientes/transfer", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Error transfiriendo cliente");
        }
        const data = await res.json();

        // Delete additional clients marked in red
        for (const id of deleteIds) {
          await apiFetch(`/api/clientes/${id}`, {
            method: "DELETE",
            credentials: "include",
          });
        }

        onResolved && onResolved({ kept: "incoming", created: data });
        onClose();
      } catch (err) {
        console.error("transfer error", err);
        alert(err.message || "Error al transferir");
      } finally {
        setLoading(false);
      }
    }
  };

  if ((!existing || (Array.isArray(existing) && existing.length === 0)) || !incoming || !target) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
    >
      <DialogTitle sx={{ pt: 0.5, pb: 0.5, fontSize: "1.25rem" }}>
        Conflicto al editar: número duplicado
      </DialogTitle>
      <DialogContent sx={{ p: 0, pt: 0, fontSize: "16px" }}>
        <Box sx={{ display: "flex", gap: 2, p: 1, minHeight: 200 }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            {/* Left: actions/options */}
            <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontSize: "0.9rem", height: 24, display: 'flex', alignItems: 'center' }}>
              Acciones
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box onClick={() => handleModeChange("apply_changes")} sx={actionCardStyle("apply_changes")}>
                <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>
                  Aplicar cambios
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                  {isSeller 
                    ? "Se aplicarán los cambios al cliente que estás editando. Podés seleccionar para eliminar solo los clientes que te pertenecen."
                    : "Si no seleccionás ningún cliente de la lista, se aplicarán los cambios creando un duplicado. Si seleccionás alguno, ese cliente será eliminado."
                  }
                </Typography>
              </Box>

              {/* Transfer option - only for admin */}
              {!isSeller && (
                <Box onClick={() => handleModeChange("transfer")} sx={actionCardStyle("transfer")}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>
                    Transferir
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
                    El primer cliente que selecciones (verde) será el origen de la transferencia.
                    Los siguientes que selecciones (rojo) serán eliminados.
                  </Typography>
                </Box>
              )}

              {/* Legend for transfer mode */}
              {selected === "transfer" && !isSeller && (
                <Box sx={{ mt: 1, p: 1, background: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Leyenda:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(76, 175, 80, 0.3)', border: '2px solid #4caf50' }} />
                    <Typography variant="caption">Transferir desde este cliente</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
                    <Typography variant="caption">Será eliminado</Typography>
                  </Box>
                </Box>
              )}

              {/* Legend for apply_changes mode */}
              {selected === "apply_changes" && (
                <Box sx={{ mt: 1, p: 1, background: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Leyenda:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(244, 67, 54, 0.3)', border: '2px solid #f44336' }} />
                    <Typography variant="caption">Será eliminado</Typography>
                  </Box>
                  {isSeller && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: 1, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.2)', opacity: 0.6 }} />
                      <Typography variant="caption">No te pertenece (no se puede seleccionar)</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: 1.5 }}>
            {/* Right: existing list (scrollable) */}
            <Typography variant="subtitle2" sx={{ color: "#0d47a1", fontSize: "0.9rem", height: 24, display: 'flex', alignItems: 'center' }}>
              Clientes existentes con mismo número
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              {existingList.map((ex) => (
                <Box 
                  key={ex.id} 
                  onClick={() => handleClientClick(ex.id)}
                  sx={getClientCardStyle(ex.id)}
                >
                  {/* Header row: client name, vendor */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                      Cliente: {ex.nombre}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      pertenece a <strong>{ex.vendedor?.nombre ?? ''}</strong>
                    </Typography>
                    {/* Status indicator */}
                    {selected === "transfer" && !isSeller && transferTargetId === ex.id && (
                      <Typography sx={{ ml: 'auto', color: '#4caf50', fontWeight: 600, fontSize: '0.8rem' }}>
                        ● Transferir desde aquí
                      </Typography>
                    )}
                    {selected === "transfer" && !isSeller && deleteIds.has(ex.id) && (
                      <Typography sx={{ ml: 'auto', color: '#f44336', fontWeight: 600, fontSize: '0.8rem' }}>
                        ● Será eliminado
                      </Typography>
                    )}
                    {selected === "apply_changes" && selectedForDelete.has(ex.id) && (
                      <Typography sx={{ ml: 'auto', color: '#f44336', fontWeight: 600, fontSize: '0.8rem' }}>
                        ● Será eliminado
                      </Typography>
                    )}
                  </Box>
                  {/* Client details in 2-column rows */}
                  {renderClientCard(ex)}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleContinue} disabled={loading}>
          {selected === "transfer" ? "Transferir" : "Aplicar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
